/** external **/
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import _ from "lodash";
import ACLStore from "lib/aclStore";
import { compose, graphql } from "lib/apolloClient";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined } from "@toddle-edu/ds-icons";

/** internal **/
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getAIConversationsFromCache } from "Worksheet/modules/WorksheetGraphqlHelpers";
import { getAIConversationsQuery } from "Worksheet/modules/WorksheetQueries";
import AIDetailedPromptModal from "AppComponents/AIFeedbackStrip/components/AIDetailedPromptModal";

/** relative **/
import {
  getAiConversationId,
  getWorksheetAiDebugInfo,
} from "../../modules/WorksheetUtils";

/**
 * Component to display debug information triggers for AI Question Creation and Document Extraction.
 *
 * @param {Object} props - The component props.
 */
const WorksheetDebugModeInfo = React.memo(props => {
  const {
    isGeneratingAIQuestions,
    aiDebugModeEnable,
    aiConversations,
    extractionAIConversations,
    getAIConversationsRefetch,
    getExtractionAIConversationsRefetch,
  } = props;

  const [wsDebugInfo, setWsDebugInfo] = useState(null);

  useEffect(() => {
    if (aiDebugModeEnable) {
      getAIConversationsRefetch();
      getExtractionAIConversationsRefetch();
    }
  }, [isGeneratingAIQuestions, aiDebugModeEnable]);

  const toggleInfoModal = useCallback(_debugInfo => {
    setWsDebugInfo(_debugInfo);
  }, []);

  if (!aiDebugModeEnable) return null;

  const debugInfos = getWorksheetAiDebugInfo({
    aiConversations,
    extractionAIConversations,
  });

  return (
    <>
      {_.isObject(wsDebugInfo) && _.has(wsDebugInfo, "usageMetricsId") ? (
        <AIDetailedPromptModal
          {...wsDebugInfo}
          aiConversationId={_.get(wsDebugInfo, "aiConversationId", "")}
          onModalClose={() => toggleInfoModal(null)}
          sourceFrom={"LESSON_PLANNING_ASSISTANT"}
        />
      ) : null}
      {_.map(debugInfos, debugInfo => {
        const key = _.get(debugInfo, "usageMetricsId", "");
        return (
          <Tooltip
            key={key}
            showArrow={true}
            tooltip={_.get(debugInfo, "tooltip", "")}
          >
            <IconButton
              dsVersion="2.0"
              size="medium"
              variant="neutral"
              type="outlined"
              _legacy_variant="outlined-subtle"
              _legacy_size="medium"
              icon={<InformationOutlined variant="subtle" />}
              testId={`debugInfo-header-worksheet-wsAiDebugMode-${key}`}
              onClick={() => toggleInfoModal(_.omit(debugInfo, "tooltip"))}
            />
          </Tooltip>
        );
      })}
    </>
  );
});

WorksheetDebugModeInfo.displayName = "WorksheetDebugModeInfo";

WorksheetDebugModeInfo.propTypes = {
  getAssessmentRefetch: PropTypes.func,
  aiConversation: PropTypes.object,
  getAIConversationsRefetch: PropTypes.func,
  extractionAIConversation: PropTypes.object,
  getExtractionAIConversationsRefetch: PropTypes.func,
  isToddleLibrary: PropTypes.bool,
};

WorksheetDebugModeInfo.defaultProps = {
  getAssessmentRefetch: _.noop,
  aiConversation: {},
  getAIConversationsRefetch: _.noop,
  extractionAIConversation: {},
  getExtractionAIConversationsRefetch: _.noop,
  isToddleLibrary: false,
};

const mapStateToProps = (state, ownProps) => {
  const { assessmentId, academicYearId } = ownProps;

  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const aiDebugModeEnable =
    ACLStore.can("IdentityAccess:EnableAIDebugMode") &&
    !isGeneratingAIQuestions;

  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const assessmentDetails = getAssessmentDetailsFromCache({
    id: assessmentId,
    collaboratorsFilter,
  });

  const aiConversationId = getAiConversationId(
    assessmentDetails,
    "TASK_CREATION"
  );
  const extractionConversationId = getAiConversationId(
    assessmentDetails,
    "WORKSHEET_EXTRACTION"
  );

  return {
    isGeneratingAIQuestions,
    aiDebugModeEnable,
    aiConversationId,
    extractionConversationId,
  };
};

export default compose(
  connect(mapStateToProps, null),

  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ aiConversationId, aiDebugModeEnable, isToddleLibrary }) =>
      !aiDebugModeEnable || _.isEmpty(aiConversationId) || isToddleLibrary,
    options: ({ aiConversationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId, portalType: "PLANNER" },
      context: { useHttpLink: true },
    }),
    props({ getAIConversations }) {
      const aiConversations = getAIConversationsFromCache(
        getAIConversations.variables
      );
      return {
        aiConversations,
        getAIConversationsRefetch: getAIConversations?.refetch,
      };
    },
  }),

  graphql(getAIConversationsQuery, {
    name: "getExtractionAIConversations",
    skip: ({ extractionConversationId, aiDebugModeEnable, isToddleLibrary }) =>
      !aiDebugModeEnable ||
      _.isEmpty(extractionConversationId) ||
      isToddleLibrary,
    options: ({ extractionConversationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: extractionConversationId, portalType: "PLANNER" },
      context: { useHttpLink: true },
    }),
    props({ getExtractionAIConversations }) {
      const extractionAIConversations = getAIConversationsFromCache(
        getExtractionAIConversations.variables
      );
      return {
        extractionAIConversations,
        getExtractionAIConversationsRefetch:
          getExtractionAIConversations?.refetch,
      };
    },
  })
)(WorksheetDebugModeInfo);
